import React, { useEffect, useRef, useState } from "react";
import HoverFullText from "../products/HoverToolkit";
import { formatInvalidLabel } from "../../constants/scanValidation";

const WrongScansTable = ({ wrongScans, handleWrongRemove, handleWrongSetQuantity }) => {
    const [editingUpc, setEditingUpc] = useState(null);
    const [editValue, setEditValue] = useState("");
    const inputRef = useRef(null);

    const totalQuantity = wrongScans.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0
    );

    useEffect(() => {
        if (editingUpc) inputRef.current?.focus();
    }, [editingUpc]);

    const startEdit = (item) => {
        setEditingUpc(item.upc);
        setEditValue(String(item.quantity));
    };

    const cancelEdit = () => {
        setEditingUpc(null);
        setEditValue("");
    };

    const commitEdit = (upc) => {
        const qty = parseInt(editValue, 10);
        if (!qty || qty < 1) {
            cancelEdit();
            return;
        }
        handleWrongSetQuantity(upc, qty);
        cancelEdit();
    };

    return (
        <div className="overflow-x-auto mt-10">
            <h2 className="text-[18px] font-semibold text-red-500 mb-4">
                Invalid Scans
            </h2>
            <table className="w-full border-collapse">
                <thead>
                    <tr className="bg-red-100 text-[14px] text-red-700">
                        <th className="p-3 th-center rounded-l-xl">No.</th>
                        <th className="p-3 th-center">UPC</th>
                        <th className="p-3 th-center">Quantity</th>
                        <th className="p-3 th-center">Description</th>
                        <th className="p-3 th-center rounded-r-xl">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {wrongScans.length === 0 && (
                        <tr>
                            <td colSpan="5" className="text-center p-6 text-gray-400">
                                No Invalid scans
                            </td>
                        </tr>
                    )}
                    {wrongScans.map((item, index) => {
                        const isEditing = editingUpc === item.upc;

                        return (
                            <tr
                                key={item.upc}
                                className="border-b last:border-b-0 hover:bg-red-50"
                            >
                                <td className="p-3 td-center">{wrongScans.length - index}</td>
                                <td className="p-3 td-num font-mono text-red-500">{item.upc}</td>
                                <td className="p-3 td-center font-semibold">
                                    {isEditing ? (
                                        <input
                                            ref={inputRef}
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    commitEdit(item.upc);
                                                }
                                                if (e.key === "Escape") cancelEdit();
                                            }}
                                            className="w-24 border-2 border-red-400 rounded-lg px-2 py-1 text-[14px] outline-none text-center"
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => startEdit(item)}
                                            className="min-w-[3rem] px-2 py-1 rounded-lg hover:bg-red-50 hover:text-red-700 tabular-nums"
                                            title="Click to edit quantity"
                                        >
                                            {item.quantity}
                                        </button>
                                    )}
                                </td>
                                <td className={`p-3 td-text ${!item.description && "text-red-400 italic"}`}>
                                    {item.description
                                        ? <HoverFullText text={item.description} sliceLength={30} />
                                        : formatInvalidLabel(item)
                                    }
                                </td>
                                <td className="p-3 td-center">
                                    <div className="flex items-center justify-center gap-3">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => commitEdit(item.upc)}
                                                    className="text-green-600 hover:text-green-800 text-[18px]"
                                                    title="Save quantity"
                                                >
                                                    <i className="fa-solid fa-check" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEdit}
                                                    className="text-gray-500 hover:text-gray-700 text-[18px]"
                                                    title="Cancel"
                                                >
                                                    <i className="fa-solid fa-xmark" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(item)}
                                                    className="text-blue-600 hover:text-blue-800 text-[18px]"
                                                    title="Edit quantity"
                                                >
                                                    <i className="fa-solid fa-pen-to-square" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleWrongRemove(item.upc)}
                                                    className="text-red-500 hover:text-red-700 text-[18px]"
                                                    title="Remove one"
                                                >
                                                    ✕
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {wrongScans.length > 0 && (
                <div className="flex justify-end mt-3 pr-3">
                    <span className="text-[14px] font-semibold text-gray-700">
                        Total Invalid:{" "}
                        <span className="text-red-500">{totalQuantity.toLocaleString()}</span>
                    </span>
                </div>
            )}
        </div>
    );
};

export default WrongScansTable;
